<template>
  <div
    class="gallery"
    :class="{
      'gallery--less-then-4-images': media && media.length < 4,
    }"
  >
    <!-- Slider -->
    <div class="g-slider">
      <img
        v-if="!media"
        class="g-image"
        src="@/assets/img/product-placeholder.jpg"
        :alt="$t('product.noImage')"
      />
      <div
        v-else-if="media?.length === 1"
        class="g-single-image"
        @click.prevent="handleSwipeClick"
      >
        <fn-zoom-image
          class="g-image"
          :src="media[0].url"
          :zoom-src="`${media[0].url}?t=prod_l`"
          :alt="media[0].altText || media[0].description"
          :enable-image-seo="enableImageSeo"
          fetchpriority="high"
          :lazy="false"
        />
      </div>
      <div
        v-else
        ref="swiper"
      >
        <div class="swiper-wrapper">
          <div
            v-for="(item, index) in media"
            :key="index"
            class="swiper-slide"
            :class="{ 'swiper-slide--rounded': rounded }"
            @click.prevent="handleSwipeClick($event, item.type)"
          >
            <fn-sovido
              v-if="item.type === 'sovido_video'"
              :product-id="productId"
            />
            <fn-youtube-video
              v-else-if="item.type === 'video'"
              :video="item.videoId"
            />
            <fn-zoom-image
              v-else
              class="g-image"
              :src="item.url"
              :zoom-src="`${item.url}?t=prod_l`"
              :alt="item.altText || item.description"
              :enable-image-seo="enableImageSeo"
              :fetchpriority="index === 0 && firstImageHasHighPrio ? 'high' : false"
              :lazy="!(index === 0 && firstImageHasHighPrio)"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="media && media.length > 1"
      class="g-footer"
    >
      <!-- Pagination -->
      <div class="g-pagination">
        <fn-dot-pagination
          :pages="media.length"
          :active-page="activeIndex + 1"
          @select="(pageNumber) => slideTo(pageNumber - 1)"
        />
      </div>

      <!-- Navigation -->
      <div
        v-if="media.length > 4"
        class="g-navigation"
      >
        <fn-button-round
          :disabled="firstSlide"
          @click="slidePrev"
        >
          <fn-icon
            icon="arrow-up"
            color="white"
            size="xxs"
          />
        </fn-button-round>

        <fn-button-round
          :disabled="lastSlide"
          @click="slideNext"
        >
          <fn-icon
            icon="arrow-down"
            color="white"
            size="xxs"
          />
        </fn-button-round>
      </div>

      <!-- Thumbs -->
      <div
        v-if="media"
        class="g-thumbs"
        :class="{
          'g-thumbs--reached-start': firstSlide,
          'g-thumbs--reached-end': lastSlide,
        }"
      >
        <div
          ref="swiperThumbs"
          class="swiper-thumbs swiper-container"
        >
          <div class="swiper-wrapper">
            <div
              v-for="(item, index) in media"
              :key="index"
              class="swiper-slide"
            >
              <fn-thumb
                v-if="item.type === 'sovido_video'"
                video-thumb-large
                :active="activeIndex === index"
                :src="item.coverImage"
                :alt="item.altText || item.description"
                :width="320"
                :height="180"
              />
              <fn-thumb
                v-else-if="item.type === 'video'"
                video-thumb
                :active="activeIndex === index"
                :src="`//img.youtube.com/vi/${item.videoId}/mqdefault.jpg`"
                :alt="item.altText || item.description"
                :width="320"
                :height="180"
              />
              <fn-thumb
                v-else
                :active="activeIndex === index"
                :src="`${item.url}?t=prod_xxs`"
                :alt="item.altText || item.description"
                :width="80"
                :height="80"
              />
            </div>
          </div>
        </div>

        <div
          v-if="media && media.length > 4"
          class="g-page-number"
        >
          {{ activeIndex + 1 }} / {{ media.length }}
        </div>
      </div>
    </div>

    <fn-sovido
      v-if="sovidoEnabled && productId"
      class="g-sovido"
      :product-id="productId"
    />

    <teleport
      v-if="media?.length && modalVisible"
      to="#teleports"
    >
      <fn-gallery-modal
        :media="media"
        :active-parent-index="activeIndex"
        :product-id="productId"
        @close="modalVisible = false"
      >
        <slot />
      </fn-gallery-modal>
    </teleport>
  </div>
</template>

<script>
import SwiperCore, { Thumbs, Controller } from 'swiper/core';

SwiperCore.use([Thumbs, Controller]);

export default {
  props: {
    media: {
      type: Array,
      default: () => [],
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    enableImageSeo: {
      type: Boolean,
      default: false,
    },
    firstImageHasHighPrio: {
      type: Boolean,
      default: false,
    },
    productId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      activeIndex: 0,
      modalVisible: false,
      swiper: null,
      swiperThumbs: null,
    };
  },
  computed: {
    ...mapState(useMainStore, {
      sovidoEnabled: 'sovidoEnabled',
    }),

    firstSlide() {
      return this.activeIndex === 0;
    },
    lastSlide() {
      return this.activeIndex === this.media.length - 1;
    },
  },
  mounted() {
    if (this.media?.length > 0) {
      this.swiperThumbs = new SwiperCore(this.$refs.swiperThumbs, {
        direction: 'vertical',
        slidesPerView: 'auto',
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
      });

      this.swiper = new SwiperCore(this.$refs.swiper, {
        on: {
          slideChangeTransitionStart: () => {
            this.activeIndex = this.swiper.realIndex;
          },
        },
        thumbs: {
          swiper: this.swiperThumbs,
        },
      });
    }
  },
  methods: {
    slideTo(index) {
      this.swiper.slideTo(index);
    },
    slideNext() {
      this.swiper.slideNext();
    },
    slidePrev() {
      this.swiper.slidePrev();
    },

    handleSwipeClick(event, clickedItemType) {
      if (clickedItemType === 'video' || clickedItemType === 'sovido_video') {
        return;
      }

      if (window.matchMedia(MEDIA_MIN_M).matches) {
        this.modalVisible = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.swiper-container {
  margin-left: auto;
  margin-right: auto;
  position: relative;
  overflow: hidden;
  list-style: none;
  padding: 0;
  z-index: 1;

  &.swiper-container-vertical {
    & > .swiper-wrapper {
      -webkit-box-orient: vertical;
      -webkit-box-direction: normal;
      flex-direction: column;
    }
  }
}

.swiper-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 1;
  display: flex;
  align-items: center;
  transition-property: transform;
  box-sizing: content-box;
  transform: translate3d(0, 0, 0);
}

.swiper-slide {
  flex-shrink: 0;
  width: 100%;
  height: 100%;
  position: relative;
  transition-property: transform;
  transform: translate3d(0, 0, 0);
}

.gallery {
  @include bp(m) {
    display: flex;
    max-height: 420px;
  }

  .g-slider {
    overflow: hidden;

    @include bp(m) {
      flex-grow: 0;
      flex-shrink: 0;
      flex-basis: span-columns(10);
      order: 1;
      max-width: 420px;
    }
  }

  .g-footer {
    position: relative;
    margin-top: $pad;

    @include bp(m) {
      flex: 0 0 68px;
      margin-top: 0;
      margin-right: $pad;
      order: 0;
      max-height: 420px;
    }
  }

  .g-navigation {
    display: none;
    flex-direction: column;
    justify-content: space-between;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    width: $space;
    margin: 0 auto;
    z-index: 1;

    @include bp(m) {
      display: flex;
    }
  }

  .g-page-number {
    font-size: map-get($font-sizes, 's');
    color: palette(grey);
    position: absolute;
    bottom: -60px;
    text-align: center;
    right: 0;
    left: 0;
  }

  .g-pagination {
    @include bp(m) {
      display: none;
    }
  }

  .g-single-image {
    text-align: center;
  }

  .swiper-thumbs {
    @include bp(m) {
      height: 100%;

      .swiper-slide {
        flex: 0 0 75px;
      }
    }
  }

  .swiper-slide {
    text-align: center;

    &--rounded,
    &--rounded > .g-image {
      border-radius: 8px;
      overflow: hidden;
    }
  }

  .g-image {
    height: 350px;
    width: 350px;

    @include bp(m) {
      width: 100%;
      height: 100%;
      max-height: 420px;
      max-width: 420px;
    }
  }

  .g-sovido {
    visibility: hidden !important;
    height: 0 !important;
  }
  &.gallery--less-then-4-images {
    .g-thumbs {
      margin: 0;

      &:before,
      &:after {
        opacity: 0;
      }
    }
  }
}

.g-thumbs {
  position: relative;
  z-index: 2;
  margin: $pad * 2 0;
  display: none;
  height: calc(100% - #{$pad * 4});

  @include bp(m) {
    display: block;
  }

  &:before,
  &:after {
    content: '';
    width: 100%;
    position: absolute;
    display: block;
    height: 50px;
    left: 0;
    right: 0;
    z-index: 3;
    pointer-events: none;
    transition: 0.2s ease-in all;
  }

  &:before {
    background: linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
    top: 0;
  }

  &:after {
    background: linear-gradient(0deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
    bottom: 0;
  }

  &.g-thumbs--reached-start {
    &:before {
      opacity: 0;
    }
  }

  &.g-thumbs--reached-end {
    &:after {
      opacity: 0;
    }
  }
}
</style>
